/**
 * Ontology JSON Import 모달.
 *
 * - 파일 업로드 또는 텍스트 붙여넣기
 * - 클라이언트에서는 최소 구조 검증만 수행 (kinds/domains/relations/shapes 모두 배열이어야 함)
 *   서버가 zod (`ONTOLOGY_SLICE_SCHEMA`) 로 재검증한다.
 */
import { useState } from "react"
import type { DomainDef, KindDef, OntologySlice, RelationDef, ShapeDef } from "../../api/admin-client.ts"

interface ImportJsonModalProps {
	readonly onClose: () => void
	readonly onImport: (slice: OntologySlice) => void
}

export function ImportJsonModal(props: ImportJsonModalProps) {
	const [raw, setRaw] = useState("")
	const [error, setError] = useState<string | undefined>()

	async function onFile(file: File) {
		const text = await file.text()
		setRaw(text)
	}

	function submit() {
		setError(undefined)
		try {
			const parsed = JSON.parse(raw) as unknown
			const slice = normalizeSlice(parsed)
			props.onImport(slice)
		} catch (err) {
			setError(`JSON 파싱 실패: ${(err as Error).message}`)
		}
	}

	return (
		<dialog
			className="modal-backdrop"
			open
			onMouseDown={(e) => e.currentTarget === e.target && props.onClose()}
		>
			<form
				className="modal"
				onMouseDown={(e) => e.stopPropagation()}
				onSubmit={(e) => {
					e.preventDefault()
					submit()
				}}
				style={{ maxWidth: 620 }}
			>
				<div className="modal-head">
					<h2>JSON 가져오기</h2>
					<p>
						파일을 선택하거나 JSON 을 붙여넣으세요. 신 슬라이스 shape (kinds/domains/relations/shapes 배열) 만
						허용됩니다.
					</p>
				</div>

				<div className="modal-body">
					{error && (
						<div className="alert alert-danger" style={{ marginBottom: 14 }}>
							{error}
						</div>
					)}

					<label className="field">
						<span className="field-label">파일</span>
						<input
							type="file"
							accept="application/json,.json"
							onChange={(e) => {
								const file = e.target.files?.[0]
								if (file) void onFile(file)
							}}
						/>
					</label>

					<label className="field">
						<span className="field-label">JSON</span>
						<textarea
							className="input"
							spellCheck={false}
							value={raw}
							onChange={(e) => setRaw(e.target.value)}
							rows={12}
							style={{ minHeight: 240 }}
						/>
					</label>
				</div>

				<div className="modal-foot">
					<button type="button" className="btn btn-ghost" onClick={props.onClose}>
						취소
					</button>
					<button type="submit" className="btn btn-primary" disabled={raw.trim().length === 0}>
						가져오기
					</button>
				</div>
			</form>
		</dialog>
	)
}

/**
 * 입력 JSON 을 정규화. 신 슬라이스 shape (kinds[], domains[], relations[], shapes[]) 만 허용.
 * 옛 형식(allowedKinds / enforcement 등) 은 지원하지 않는다 — 의도된 브레이킹 체인지.
 */
function normalizeSlice(raw: unknown): OntologySlice {
	if (!raw || typeof raw !== "object") throw new Error("object 가 아닙니다")
	const obj = raw as Record<string, unknown>

	return {
		...(typeof obj.presetId === "string" ? { presetId: obj.presetId } : {}),
		kinds: parseKinds(obj.kinds),
		domains: parseDomains(obj.domains),
		relations: parseRelations(obj.relations),
		shapes: parseShapes(obj.shapes)
	}
}

function parseKinds(value: unknown): ReadonlyArray<KindDef> {
	if (!Array.isArray(value)) return []
	return value.map<KindDef>((k) => ({
		name: String((k as KindDef).name),
		displayName: String((k as KindDef).displayName ?? (k as KindDef).name),
		...((k as KindDef).description ? { description: (k as KindDef).description } : {}),
		...((k as KindDef).color ? { color: (k as KindDef).color } : {}),
		...((k as KindDef).icon ? { icon: (k as KindDef).icon } : {}),
		...((k as KindDef).category ? { category: (k as KindDef).category } : {})
	}))
}

function parseDomains(value: unknown): ReadonlyArray<DomainDef> {
	if (!Array.isArray(value)) return []
	return value.map<DomainDef>((d) => ({
		name: String((d as DomainDef).name),
		displayName: String((d as DomainDef).displayName ?? (d as DomainDef).name),
		...((d as DomainDef).description ? { description: (d as DomainDef).description } : {}),
		...((d as DomainDef).color ? { color: (d as DomainDef).color } : {})
	}))
}

function parseRelations(value: unknown): ReadonlyArray<RelationDef> {
	if (!Array.isArray(value)) return []
	return value.map<RelationDef>((r) => ({
		name: String((r as RelationDef).name),
		displayName: String((r as RelationDef).displayName ?? (r as RelationDef).name),
		...((r as RelationDef).description ? { description: (r as RelationDef).description } : {}),
		...((r as RelationDef).color ? { color: (r as RelationDef).color } : {}),
		...((r as RelationDef).directionality ? { directionality: (r as RelationDef).directionality } : {}),
		...((r as RelationDef).inverseOf ? { inverseOf: (r as RelationDef).inverseOf } : {})
	}))
}

function parseShapes(value: unknown): ReadonlyArray<ShapeDef> {
	if (!Array.isArray(value)) return []
	return value.map<ShapeDef>((s) => ({
		sourceKind: String((s as ShapeDef).sourceKind),
		relation: String((s as ShapeDef).relation),
		targetKind: String((s as ShapeDef).targetKind),
		...((s as ShapeDef).description ? { description: (s as ShapeDef).description } : {})
	}))
}
